import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { motion } from 'motion/react';
import { StringParam, useQueryParam } from 'use-query-params';
import { VisuallyLogo } from '@/features/strapi/components/logo';
import { LogoUploadDropdown } from '@/components/logo-upload-dropdown';
import { getAliasQueryParam, nav } from '@/utils/browser';
import { AccountContext, useFeatureBit } from '@/features/account-context';
import { FeatureBit, StoreDetails } from '@/webapi/use-auth-api';
import { breakpoints } from '@/components/responsive';

const accountDomainTooltip = `account-domain-tooltip`;
const logoCachePrefix = `vsly_logo_cache`;
const siteNameCachePrefix = `vsly_site_name_cache`;

interface UploadedShopLogoState {
  alias: string;
  logo?: string;
}

interface SiteNameState {
  alias: string;
  siteName?: string;
}

interface LogoSectionProps {
  store?: StoreDetails;
}

const getLogoCacheKey = (alias: string) => `${logoCachePrefix}_${alias}`;

const getSiteNameCacheKey = (alias: string) =>
  `${siteNameCachePrefix}_${alias}`;

const normalizeLogo = (logo?: string | null) => {
  const normalizedLogo = logo?.trim();
  return normalizedLogo || undefined;
};

const normalizeText = (value?: string | null) => {
  const normalizedValue = value?.trim();
  return normalizedValue || undefined;
};

const getSiteName = (domain?: string) =>
  domain
    ?.replace(`https://`, ``)
    ?.replace(`www.`, ``)
    ?.replace(`.myshopify.com`, ``);

const getCachedUploadedShopLogo = (alias: string) => {
  if (!alias || typeof window === `undefined`) return undefined;

  try {
    const value = localStorage.getItem(getLogoCacheKey(alias));
    if (value === null) {
      cacheUploadedShopLogo(alias, null);
      return undefined;
    }

    return normalizeLogo(JSON.parse(value));
  } catch {
    return undefined;
  }
};

const getCachedSiteName = (alias: string) => {
  if (!alias || typeof window === `undefined`) return undefined;

  try {
    const value = localStorage.getItem(getSiteNameCacheKey(alias));
    if (value === null) return undefined;

    return normalizeText(JSON.parse(value));
  } catch {
    return undefined;
  }
};

const cacheUploadedShopLogo = (
  alias: string,
  logo?: string | null,
): boolean => {
  if (!alias || typeof window === `undefined`) return false;

  try {
    localStorage.setItem(
      getLogoCacheKey(alias),
      JSON.stringify(normalizeLogo(logo) || null),
    );
    return true;
  } catch {
    return false;
  }
};

const cacheSiteName = (alias: string, siteName?: string | null): boolean => {
  if (!alias || typeof window === `undefined`) return false;

  try {
    localStorage.setItem(
      getSiteNameCacheKey(alias),
      JSON.stringify(normalizeText(siteName) || null),
    );
    return true;
  } catch {
    return false;
  }
};

export const LogoSection: FC<LogoSectionProps> = ({ store }) => {
  const hideBrandLogo = useFeatureBit(FeatureBit.HIDE_BRAND_LOGO);
  const { generalStoreSettings, setGeneralStoreSettings } =
    useContext(AccountContext);
  const [aliasQueryParam] = useQueryParam(`al`, StringParam);
  const logoAlias =
    getAliasQueryParam() || aliasQueryParam || store?.alias || ``;
  const isStoreAliasReady = !logoAlias || store?.alias === logoAlias;
  const [uploadedShopLogoState, setUploadedShopLogoState] =
    useState<UploadedShopLogoState>(() => ({
      alias: logoAlias,
      logo: getCachedUploadedShopLogo(logoAlias),
    }));
  const [siteNameState, setSiteNameState] = useState<SiteNameState>(() => ({
    alias: logoAlias,
    siteName: getCachedSiteName(logoAlias),
  }));
  const [shopifyLogo, setShopifyLogo] = useState<string | undefined>(
    generalStoreSettings?.shop_logo || undefined,
  );
  const uploadedShopLogo =
    uploadedShopLogoState.alias === logoAlias
      ? uploadedShopLogoState.logo
      : getCachedUploadedShopLogo(logoAlias);

  const siteName = useMemo(() => getSiteName(store?.domain), [store]);
  const cachedSiteName =
    siteNameState.alias === logoAlias
      ? siteNameState.siteName
      : getCachedSiteName(logoAlias);

  const storeDomain = useMemo(
    () => store?.domain?.replace(`https://`, ``)?.replace(`www.`, ``),
    [store],
  );

  const shouldShowSiteNameTooltip = useMemo(
    () => isStoreAliasReady && (storeDomain?.length || 0) > 20,
    [isStoreAliasReady, storeDomain],
  );
  const displayedSiteName = isStoreAliasReady ? siteName : cachedSiteName;
  const displayedStoreDomain = isStoreAliasReady ? storeDomain : ``;

  useEffect(() => {
    setUploadedShopLogoState({
      alias: logoAlias,
      logo: getCachedUploadedShopLogo(logoAlias),
    });
    setSiteNameState({
      alias: logoAlias,
      siteName: getCachedSiteName(logoAlias),
    });
    setShopifyLogo(undefined);
  }, [logoAlias]);

  useEffect(() => {
    if (!isStoreAliasReady) return;

    cacheSiteName(logoAlias, siteName);
    setSiteNameState({
      alias: logoAlias,
      siteName: normalizeText(siteName),
    });
  }, [isStoreAliasReady, siteName]);

  useEffect(() => {
    if (!hideBrandLogo && generalStoreSettings && store?.alias === logoAlias) {
      const fetchedLogo = normalizeLogo(
        generalStoreSettings.uploaded_shop_logo,
      );
      cacheUploadedShopLogo(logoAlias, fetchedLogo);
      setUploadedShopLogoState({
        alias: logoAlias,
        logo: fetchedLogo,
      });
      setShopifyLogo(generalStoreSettings.shop_logo);
    }
  }, [generalStoreSettings]);

  const onLogoUpdate = (logo: string | undefined) => {
    const nextLogo = normalizeLogo(logo);
    cacheUploadedShopLogo(logoAlias, nextLogo);
    setUploadedShopLogoState({
      alias: logoAlias,
      logo: nextLogo,
    });
    if (setGeneralStoreSettings && generalStoreSettings) {
      setGeneralStoreSettings({
        ...generalStoreSettings,
        uploaded_shop_logo: nextLogo,
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Container>
        <VisuallyLogoButton onClick={() => nav(`/dashboard`)}>
          <VisuallyLogo small />
        </VisuallyLogoButton>
        {hideBrandLogo ? (
          <SiteName data-tip data-for={accountDomainTooltip}>
            {displayedSiteName || ``}
          </SiteName>
        ) : (
          <LogoUploadDropdown
            storeAlias={logoAlias}
            storeDomain={displayedStoreDomain || ``}
            shopifyLogo={shopifyLogo}
            uploadedShopLogo={uploadedShopLogo}
            currentSettings={
              isStoreAliasReady ? generalStoreSettings : undefined
            }
            onLogoUpdated={onLogoUpdate}
          >
            {uploadedShopLogo ? (
              <BrandLogo
                src={uploadedShopLogo}
                alt={displayedSiteName || `Store logo`}
                data-tip
                data-for={accountDomainTooltip}
              />
            ) : (
              <SiteName data-tip data-for={accountDomainTooltip}>
                {displayedSiteName || ``}
              </SiteName>
            )}
          </LogoUploadDropdown>
        )}
        {shouldShowSiteNameTooltip && (
          // @ts-ignore
          <ReactTooltip
            id={accountDomainTooltip}
            place="bottom"
            backgroundColor="#fff"
            textColor="#000"
            className="domain-tooltip"
          >
            {displayedStoreDomain}
          </ReactTooltip>
        )}
      </Container>
    </motion.div>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
`;

const VisuallyLogoButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const SiteName = styled.span`
  font-family: 'Inter', serif;
  font-size: 1.8rem;
  text-transform: uppercase;
  font-weight: bold;
  color: black;
  max-width: 24rem;
  letter-spacing: -0.79px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BrandLogo = styled.img`
  display: block;
  ${breakpoints.down(`md`)} {
    transform: scale(0.8);
  }
`;
